import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ISchemaDataGrid } from './edit-table-grid.model';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { Subject, takeUntil } from 'rxjs';
import { StylesService } from '@shared/service/app/styles.service';
import { CalculadorService } from '@shared/service/app/calculador.service';

@Component({
  selector: 'app-edit-table-grid',
  templateUrl: './edit-table-grid.component.html',
  styleUrls: ['./edit-table-grid.component.scss']
})
export class EditTableGridComponent implements OnInit {
  color_primario: string;
  color_secundario: string;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _empresaService: EmpresaService,
    private _stylesService: StylesService,
    private _calculadorService: CalculadorService,
  ) {
    // Subscribe to empresa data
    this._empresaService.empresa$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(empresa => {
        this.color_primario = empresa.color_primario;
        this.color_secundario = empresa.color_secundario;
      });
  }


  @Input() dataSource: any[] = [];
  @Input() columnsSchema: ISchemaDataGrid[] = [];
  // Estilos que puede pasar el padre
  @Input() customContainerStyles: Partial<CSSStyleDeclaration> = {}; // recibe opcionalmente estilos
  @Input() headerStyles: Partial<CSSStyleDeclaration> = {};
  @Input() cellStyles: Partial<CSSStyleDeclaration> = {};
  @Input() cardStyles: Partial<CSSStyleDeclaration> = {};

  @Output() dataChange = new EventEmitter<any[]>();

  displayedColumns: any = [];
  displayedColumnsLabels: any = [];

  existTotalRow: boolean = false;

  ngOnInit() {
    this.displayedColumns = this.columnsSchema.filter(item => item.visible !== false).map(item => item.key);
    this.displayedColumnsLabels = this.columnsSchema.map(item => item.label);
    this.existTotalRow = this.columnsSchema.some(item => item.isTotalCount);

    // Recorro el arreglo de datos
    this.dataSource.forEach(row => {
      this.columnsSchema.forEach(col => {
        // Verfico si hay un combo select
        if (col.type === 'select') {
          // seteo el primer valor inicial
          this.setDefaultSelect(col, row);
        }
      });
    });
  }

  onValueChange() {
    this.dataChange.emit(this.dataSource)
  }

  getTotal(column: string) {
    return this._calculadorService.calcular(this.dataSource, column, 'sum');
  }

  getContainerStyle() {
    const defaultContainerStyles: Partial<CSSStyleDeclaration> = {
      backgroundColor: this.color_primario ?? '#f5f5f5',
      color: '#333',
      border: '1px solid #ddd',
      borderRadius: '8px',
      justifyContent: 'flex-start',
      alignItems: 'center'
    };

    return {
      ...defaultContainerStyles,
      ...this.customContainerStyles // sobrescribe si el padre pasó algo
    };
  }

  // Método que combina default + custom
  getHeaderStyle(): Partial<CSSStyleDeclaration> {
    // Estilo por default para cabecera
    const defaultHeaderStyles: Partial<CSSStyleDeclaration> = {
      backgroundColor: this.color_primario ?? '#1976d2', // azul por default
      color: 'white',
      textAlign: 'center',     // centra horizontal
    };

    return {
      ...defaultHeaderStyles,
      ...this.headerStyles
    };
  }

  // Método que combina default + estilos opcionales del padre
  getCellStyle(): Partial<CSSStyleDeclaration> {
    // Estilos por defecto de las celdas
    const defaultCellStyles: Partial<CSSStyleDeclaration> = {
      textAlign: 'center',     // centra horizontal
      verticalAlign: 'middle', // centra vertical
      padding: '8px',
      textAlignLast: "center"
    };

    return {
      ...defaultCellStyles,
      ...this.cellStyles
    };
  }

  // Estilos de la card
  getCardStyle(index: number): Partial<CSSStyleDeclaration> {
    // Estilos por defecto de la card
    const defaultCardStyles: Partial<CSSStyleDeclaration> = {
      padding: '1rem',
      borderRadius: '0.5rem',
      boxShadow: `0 4px 6px ${(this._stylesService.hexToRgba(this.color_primario, 0.15))}`,
      borderTopWidth: '0.25rem',
      borderTopColor: this._stylesService.hexToRgba(this.color_primario, 0.15), // alterna color superior
      backgroundColor: index % 2 === 0 ? '#f9fafb' : '#F2F2F2',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem'
    };

    // Combina default + estilos opcionales recibidos del padre
    return {
      ...defaultCardStyles,
      ...this.cardStyles
    };
  }


  // Estilos para filas de contenido dentro de la card
  getRowStyle() {
    return {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    };
  }

  // Estilo para inputs y selects dentro de la card
  getInputStyle() {
    return {
      width: '100%',
    };
  }

  formatValue(col: any, element: any): string {
    if (!element[col.key]) return '-';
    if (col?.format === 'currency') {
      return '$' + new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(element[col.key]);
    }
    return element[col.key];
  }

  setDefaultSelect(col: ISchemaDataGrid, element: any) {
    const data = col.selectConfig?.dataValue ?? element[col.selectConfig?.dataReference || ''];
    if (!element[col.key] && data && data.length > 0) {
      let x = 0;
      // Si existe una clave default, la seteo
      if (col.selectConfig.defaultKey) {
        x = data.find(item => item[col.selectConfig.defaultKey] === element[col.selectConfig.defaultKey]);
      }

      element[col.key] = x; // ✅ primera opción automática
    }
  }
}
